import { CustomerFeedback, FilteredCreditApplication, RequiredDocumentWithStatus } from '@applyonline/shared'
import { compareAsc } from '@if/core/date'
import { EApplicantType, EContractStatus, EExternalStep, EExternalStepStatus } from '@if/core/types'
import {
    hasApplicantSubmittedAllRequiredIncomeDocuments,
    hasApplicantSubmittedFlinks,
} from '../../../data/credit-application-selectors'
import { useMemo, useState } from 'react'
import { CreditStepStatus } from './OverviewPage/components/CreditStep'

const contractIsReceivedStatusSet = new Set([
  EContractStatus.UnderReview,
  EContractStatus.Completed,
  EContractStatus.Declined,
])

export function contractIsReceived(
  applicantType: EApplicantType | null,
  creditApp: FilteredCreditApplication | null,
): boolean {
  if (!creditApp) return false

  return (
    contractIsReceivedStatusSet.has(creditApp.contract.status) ||
    (Boolean(applicantType) &&
      creditApp?.signers.some(
        (x) =>
          (x.isApplicant && applicantType === EApplicantType.Applicant) ||
          (!x.isApplicant && applicantType === EApplicantType.CoApplicant),
      ))
  )
}

export function hasCompletedFlinks(creditApp: FilteredCreditApplication | null): boolean {
  const found = creditApp?.requiredExternalSteps?.find(
    (x) => x.applicantType === EApplicantType.Applicant && x.externalStepId === EExternalStep.BankAccount,
  )

  if (found) {
    return found.status === EExternalStepStatus.InProgress || found.status === EExternalStepStatus.Completed
  }
  return false
}

export function isReadyForCustomerFeedback(
  creditApp: FilteredCreditApplication | null,
  skipBankAccountRequiredDocuments: RequiredDocumentWithStatus[],
): boolean {
  if (!creditApp) return false
  return (
    creditApp.allIncomesConfirmed ||
    hasApplicantSubmittedFlinks(creditApp, EApplicantType.Applicant) ||
    hasApplicantSubmittedAllRequiredIncomeDocuments(skipBankAccountRequiredDocuments, EApplicantType.Applicant)
  )
}

export function getFeedbackStepStatus(
  creditApp: FilteredCreditApplication | null,
  skipBankAccountRequiredDocuments: RequiredDocumentWithStatus[],
  feedback: CustomerFeedback | null,
) {
  if (!isReadyForCustomerFeedback(creditApp, skipBankAccountRequiredDocuments)) return CreditStepStatus.NotReady
  if (!feedback) return CreditStepStatus.RequiresAttention
  return CreditStepStatus.Completed
}

export const useIsReportExpired = (hardHitReportReceivedOn: Date) => {
  const [currentDate] = useState(new Date())

  const isExpired = useMemo(() => {
    const receivedOnPlus90Days = new Date(hardHitReportReceivedOn)
    receivedOnPlus90Days.setDate(receivedOnPlus90Days.getDate() + 90)

    return compareAsc(currentDate, receivedOnPlus90Days) === 1
  }, [currentDate, hardHitReportReceivedOn])

  return isExpired
}
